import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

const ACCESS_KEY = 'dermoscan_admin_token';
const REFRESH_KEY = 'dermoscan_admin_refresh';
const USER_KEY = 'dermoscan_admin_user';
const EXPIRY_SKEW_MS = 30_000;

interface ServiceResult<T> { isSuccess: boolean; data?: T; fail?: { message?: string }; }
interface LoginResponse { userId: string; firstName: string; lastName: string; email: string; accessToken: string; refreshToken: string; }
interface SessionUser { name: string; email: string }

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly rawHttp = new HttpClient(inject(HttpBackend));
  private readonly router = inject(Router);
  private refreshInFlight: Observable<string> | null = null;
  readonly user = signal<SessionUser | null>(this.readUser());

  login(email: string, password: string): Observable<void> {
    return this.http.post<ServiceResult<LoginResponse>>(`${environment.apiBaseUrl}/auth/login`, { email, password }).pipe(
      map(result => {
        if (!result.isSuccess || !result.data) throw new Error(result.fail?.message || 'Giriş başarısız.');
        return result.data;
      }),
      tap(data => this.persistSession(data)),
      map(() => void 0)
    );
  }

  token(): string | null { return localStorage.getItem(ACCESS_KEY); }
  refreshToken(): string | null { return localStorage.getItem(REFRESH_KEY); }

  isAdmin(): boolean {
    const token = this.token();
    return !!token && this.roles(token).includes('Admin');
  }

  isAccessTokenExpired(token = this.token(), skewMs = EXPIRY_SKEW_MS): boolean {
    if (!token) return true;
    const exp = this.payload(token)?.['exp'];
    return typeof exp !== 'number' || exp * 1000 <= Date.now() + skewMs;
  }

  refreshSession(): Observable<string> {
    if (this.refreshInFlight) return this.refreshInFlight;

    const refreshToken = this.refreshToken();
    if (!refreshToken) {
      this.endSession();
      return throwError(() => new Error('Oturum süresi doldu.'));
    }

    this.refreshInFlight = this.rawHttp.post<ServiceResult<LoginResponse>>(
      `${environment.apiBaseUrl}/auth/refresh-token-login`,
      { refreshToken }
    ).pipe(
      map(result => {
        if (!result.isSuccess || !result.data?.accessToken || !result.data.refreshToken) {
          throw new Error(result.fail?.message || 'Oturum yenilenemedi.');
        }
        return result.data;
      }),
      tap(data => this.persistSession(data)),
      map(data => data.accessToken),
      catchError(error => {
        this.endSession();
        return throwError(() => error);
      }),
      finalize(() => { this.refreshInFlight = null; }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    return this.refreshInFlight;
  }

  logout(): void {
    const refreshToken = this.refreshToken();
    const accessToken = this.token();
    if (!refreshToken) {
      this.endSession();
      return;
    }

    const revoke = (token: string, refresh: string) => this.rawHttp.post(
      `${environment.apiBaseUrl}/auth/logout`,
      { refreshToken: refresh },
      { headers: new HttpHeaders({ Authorization: `Bearer ${token}` }) }
    ).pipe(catchError(() => of(null)));

    if (accessToken && !this.isAccessTokenExpired(accessToken, 0)) {
      revoke(accessToken, refreshToken).subscribe();
      this.endSession();
      return;
    }

    this.refreshSession().pipe(
      switchMap(newAccess => revoke(newAccess, this.refreshToken() ?? refreshToken)),
      catchError(() => of(null))
    ).subscribe(() => this.endSession());
  }

  private persistSession(data: LoginResponse): void {
    if (!this.roles(data.accessToken).includes('Admin')) throw new Error('Bu panel yalnızca Admin rolüne açıktır.');
    const user = { name: `${data.firstName} ${data.lastName}`.trim(), email: data.email };
    localStorage.setItem(ACCESS_KEY, data.accessToken);
    localStorage.setItem(REFRESH_KEY, data.refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    this.user.set(user);
  }

  private endSession(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    this.user.set(null);
    void this.router.navigateByUrl('/login');
  }

  private roles(token: string): string[] {
    const raw = this.payload(token)?.['role']
      ?? this.payload(token)?.['roles']
      ?? this.payload(token)?.['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']
      ?? [];
    return Array.isArray(raw) ? raw.filter((role): role is string => typeof role === 'string') : typeof raw === 'string' ? [raw] : [];
  }

  private payload(token: string): Record<string, unknown> | null {
    try {
      const segment = token.split('.')[1];
      if (!segment) return null;
      return JSON.parse(atob(segment.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
    } catch {
      return null;
    }
  }

  private readUser(): SessionUser | null {
    try {
      const parsed = JSON.parse(localStorage.getItem(USER_KEY) || 'null') as SessionUser | null;
      return parsed?.email ? parsed : null;
    } catch {
      return null;
    }
  }
}
